<template>
  <settings-form :breadcrumbs="breadcrumbs">
    <template #actions>
      <!-- step 1: start setup (enter draft mode) -->
      <v-btn
        v-if="!isLocalePriorityMode && !isLocalePriorityDraftMode && isMessageCenterLoaded"
        class="mr-2 black--text"
        color="yellow"
        @click="startSetupConfirmVisible = true"
      >
        <v-icon class="mr-2">
          mdi-auto-fix
        </v-icon>
        {{ $t('button.mc_start_setup') }}
      </v-btn>
      <!-- TESTING ONLY - remove before the public webapp deploy: revert a promoted
           migration back to the draft step (inverse of go live) -->
      <v-btn
        v-if="false && isLocalePriorityMode && isMessageCenterLoaded"
        class="mr-2"
        outlined
        color="error"
        @click="revertConfirmVisible = true"
      >
        Revert migration (test)
      </v-btn>
      <!-- step 2: draft in progress -> go live / cancel -->
      <template v-if="isLocalePriorityDraftMode && isMessageCenterLoaded">
        <v-btn
          class="mr-2"
          color="primary"
          @click="goLiveConfirmVisible = true"
        >
          <v-icon class="mr-2">
            mdi-rocket-launch
          </v-icon>
          {{ $t('button.mc_go_live') }}
        </v-btn>
        <v-btn
          class="mr-2"
          outlined
          color="error"
          @click="cancelSetupConfirmVisible = true"
        >
          {{ $t('button.mc_cancel_setup') }}
        </v-btn>
      </template>
      <v-btn
        class="mr-2"
        color="success"
        @click="add"
      >
        <v-icon>mdi-plus</v-icon>
        {{ $t('button.new_messaging_rule') }}
      </v-btn>
    </template>
    <template #content>
      <!-- step 1: start setup -->
      <confirmation-dialog
        v-if="startSetupConfirmVisible"
        :visible="startSetupConfirmVisible"
        :title="$t('button.mc_start_setup')"
        :confirm-title="$t('button.continue')"
        confirm-color="primary"
        :cancel-title="$t('button.cancel')"
        @confirm="startSetup"
        @cancel="startSetupConfirmVisible = false"
        @close="startSetupConfirmVisible = false"
      >
        <i18n
          path="message.confirm_mc_start_setup"
          tag="div"
          class="mb-0"
        >
          <a
            :href="helpPageUrl"
            target="_blank"
            rel="noopener noreferrer"
          >{{ $t('message.confirm_set_locale_priority_mode_help_page') }}</a>
        </i18n>
      </confirmation-dialog>
      <!-- step 2: go live -->
      <confirmation-dialog
        v-if="goLiveConfirmVisible"
        :visible="goLiveConfirmVisible"
        :title="$t('button.mc_go_live')"
        :confirm-title="$t('button.continue')"
        confirm-color="primary"
        :cancel-title="$t('button.cancel')"
        @confirm="goLive"
        @cancel="goLiveConfirmVisible = false"
        @close="goLiveConfirmVisible = false"
      >
        <i18n
          path="message.confirm_mc_go_live"
          tag="div"
          class="mb-0"
        >
          <a
            :href="helpPageUrl"
            target="_blank"
            rel="noopener noreferrer"
          >{{ $t('message.confirm_set_locale_priority_mode_help_page') }}</a>
        </i18n>
      </confirmation-dialog>
      <!-- cancel setup -->
      <confirmation-dialog
        v-if="cancelSetupConfirmVisible"
        :visible="cancelSetupConfirmVisible"
        :title="$t('button.mc_cancel_setup')"
        :message="$t('message.confirm_mc_cancel_setup')"
        :confirm-title="$t('button.continue')"
        confirm-color="error"
        :cancel-title="$t('button.cancel')"
        @confirm="cancelSetup"
        @cancel="cancelSetupConfirmVisible = false"
        @close="cancelSetupConfirmVisible = false"
      />
      <!-- TESTING ONLY - remove before the public webapp deploy: revert migration -->
      <confirmation-dialog
        v-if="revertConfirmVisible"
        :visible="revertConfirmVisible"
        title="Revert migration (test)"
        :message="revertConfirmMessage"
        :confirm-title="$t('button.continue')"
        confirm-color="error"
        :cancel-title="$t('button.cancel')"
        @confirm="revertMigration"
        @cancel="revertConfirmVisible = false"
        @close="revertConfirmVisible = false"
      />
      <!-- # DEFAULT TEMPLATES -->
      <section-header :title="$t('label.default_messages')" />
      <message-center-rules-table
        :rules="builtinRules"
        @row-clicked="edit"
      />
      <!-- # CUSTOM TEMPLATES -->
      <section-header :title="$t('label.custom_messages')" />
      <message-center-rules-table
        :rules="customRules"
        show-details
        @row-clicked="edit"
      />
      <!-- /// -->
    </template>
  </settings-form>
</template>

<script lang="ts">
import Vue from 'vue';
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue';
import SettingsForm from '@/components/layouts/SettingsForm.vue';
import MessageCenterRulesTable from '@/components/views/settings/MessageCenterRulesTable.vue';
import SettingsBreadcrumbs from '@/mixins/settings-breadcrumbs';
import { performAction } from '@/services/vue-utils';
import MessageRule from '@/model/MessageRule';
import { loadStoreModule, unloadStoreModule } from '@/services/module-loader';
import MessageCenterStore from '@/store/message-center-store';
import { MessageTypeChannel } from '@/model/MessageType';
import { extraDocsLocales } from '@/plugins/i18n';

export default Vue.extend({
  name: 'MessageCenter',
  components: { SettingsForm, MessageCenterRulesTable, ConfirmationDialog },
  mixins: [SettingsBreadcrumbs],
  data() {
    return {
      breadcrumbs: [],
      startSetupConfirmVisible: false,
      goLiveConfirmVisible: false,
      cancelSetupConfirmVisible: false,
      revertConfirmVisible: false, // TESTING ONLY - remove before the public webapp deploy
      revertConfirmMessage: 'TESTING ONLY: this returns the account to the setup (draft) step. '
        + 'Your previous templates start sending again; the templates prepared in the new Message Center '
        + 'are kept as the draft — you can Go live again or Cancel setup.',
    };
  },
  computed: {
    customRules(): MessageRule[] {
      return this.$tstore.getters.messageCenterCustomRules;
    },
    builtinRules(): MessageRule[] {
      return this.$tstore.getters.messageCenterBuiltinRules;
    },
    isLocalePriorityMode(): boolean {
      return this.$tstore.getters.messageCenterIsLocalePriorityMode;
    },
    isLocalePriorityDraftMode(): boolean {
      return this.$tstore.getters.messageCenterIsLocalePriorityDraftMode;
    },
    isMessageCenterLoaded(): boolean {
      return this.$tstore.getters.isMessageCenterLoaded;
    },
    helpPageUrl(): string {
      const docsLocale = extraDocsLocales.find((l) => this.$localized.locale.startsWith(l)) || '';
      const l = docsLocale === '' ? '' : `${docsLocale}/`;
      return `https://docs.guestplan.com/#/${l}messages?id=mc-migration`;
    },
  },
  created() {
    loadStoreModule(this, 'messageCenter', MessageCenterStore, async () => { this.load(); });
  },
  beforeDestroy() {
    unloadStoreModule(this, 'messageCenter', async () => { this.$tstore.dispatch('resetMessageCenter'); });
  },
  methods: {
    async load() {
      if (!this.$tstore.getters.isMessageCenterLoaded) {
        const ok = await performAction(
          null,
          this.$i18n.tc('error.load_message_center'),
          async () => this.$tstore.dispatch('loadMessageCenter'),
        );

        if (!ok) return;

        const messageChannel = this.$route.query['message-channel'] as string;
        const evenType = this.$route.query['event-type'] as string;

        if (messageChannel && evenType) {
          const rule = this.builtinRules.find(
            (r) => r.eventType?.type === evenType && r.messageType?.channel === MessageTypeChannel.EMAIL,
          );
          if (rule) this.edit({ id: rule?.id }, undefined);
        }
      }

      // arriving from message settings (?start-setup): open the start-setup (migration)
      // dialog here, where the new templates are prepared afterwards
      if (this.$route.query['start-setup']) {
        this.$router.replace({ query: { ...this.$route.query, 'start-setup': undefined } });
        if (!this.isLocalePriorityMode && !this.isLocalePriorityDraftMode) this.startSetupConfirmVisible = true;
      }
    },
    edit(o: any, prop: any) {
      this.$router.push(`/settings/message-center/${o.id}`);
    },
    add() {
      this.$router.push('/settings/message-center/new');
    },
    async startSetup() {
      this.startSetupConfirmVisible = false;
      await performAction(
        null,
        this.$i18n.tc('error.set_locale_priority_mode'),
        async () => this.$tstore.dispatch('setLocalePriorityMode', { action: 'enter' }),
      );
    },
    async goLive() {
      this.goLiveConfirmVisible = false;
      await performAction(
        null,
        this.$i18n.tc('error.set_locale_priority_mode'),
        async () => this.$tstore.dispatch('setLocalePriorityMode', { action: 'promote' }),
      );
    },
    async cancelSetup() {
      this.cancelSetupConfirmVisible = false;
      await performAction(
        null,
        this.$i18n.tc('error.set_locale_priority_mode'),
        async () => this.$tstore.dispatch('setLocalePriorityMode', { action: 'cancel' }),
      );
    },
    // TESTING ONLY - remove before the public webapp deploy
    async revertMigration() {
      this.revertConfirmVisible = false;
      await performAction(
        null,
        this.$i18n.tc('error.set_locale_priority_mode'),
        async () => this.$tstore.dispatch('setLocalePriorityMode', { action: 'revert' }),
      );
    },
  },
});
</script>
